import React from 'react';
import SalesDetailPage from './SalesDetailPage';
import {
  PENDING_CANCELATIONS_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/pendingCancelationsColumnsConfig';
import { currencyFormatter, compactCurrency } from '../utils/reportingUtils';

const buildFilterGroups = (venueOwnerships) => {
  const groups = [];

  if (venueOwnerships && venueOwnerships.length > 0) {
    groups.push({
      label: 'Venues',
      filters: [
        {
          id: 'venue_ownership_id',
          label: 'Venue',
          type: 'checkbox',
          options: venueOwnerships.map((vo) => ({
            value: String(vo.id),
            label: vo.name || vo.venue_name || `Venue ${vo.id}`,
          })),
        },
      ],
    });
  }

  groups.push({
    label: 'Membership',
    filters: [
      {
        id: 'status',
        label: 'Status',
        type: 'checkbox',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'trialing', label: 'Trialing' },
        ],
      },
      {
        id: 'interval',
        label: 'Billing',
        type: 'checkbox',
        options: [
          { value: 'month', label: 'Monthly' },
          { value: 'year', label: 'Annual' },
        ],
      },
      { id: 'amount', label: 'Amount', type: 'range' },
    ],
  });

  return groups;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.status && activeFilters.status.length > 0) params.status = activeFilters.status;
  if (activeFilters.interval && activeFilters.interval.length > 0) params.interval = activeFilters.interval;

  const amount = activeFilters.amount;
  if (amount) {
    if (amount.min != null && amount.min !== '') params.amount_min = amount.min;
    if (amount.max != null && amount.max !== '') params.amount_max = amount.max;
  }
  return params;
};

const PENDING_CANCELATIONS_CONFIG = {
  pageTitle: 'Pending Cancelations',
  pageSubtitle: 'Members who are still active but have opted out — their membership will not renew after the current period ends.',
  sidebarKey: 'memberships_pending_cancelations',
  defaultPreset: 'year_to_date',
  // Snapshot report — no date range applies, so the picker and the "View by"
  // toggle are both hidden.
  showDatePicker: false,
  dateModeOptions: [{ key: 'order_date', label: 'Order Date' }],

  columns: PENDING_CANCELATIONS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingMembershipsPendingCancelationsColumns_${teamId}`,
  firstColumnId: 'member_name',
  defaultSortRules: [{ field: 'ends_at', direction: 'asc' }],

  kpiFromDetailResponse: true,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/memberships/pending-cancelations/data.json`,
  detailResponseKey: 'members',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/memberships/pending-cancelations/export`,
  detailErrorMessage: 'Failed to load pending cancelation data.',
  detailTableTitle: 'Pending Cancelations',
  emptyMessage: 'No members have opted out.',
  searchPlaceholder: 'Search members...',
  paginationLabel: 'members',
  itemLabel: (count) => `member${count !== 1 ? 's' : ''}`,

  buildFilterGroups,
  buildDetailApiFilters,
  filterTitle: 'Filter Members',
  filterSubtitle: 'Apply filters to narrow member data.',

  buildKpiCards: (current) => [
    { label: 'Pending Cancelations', value: (current.pending_cancelations || 0).toLocaleString() },
    { label: 'MRR at Risk', value: currencyFormatter.format(current.mrr_at_risk || 0) },
    { label: 'ARR at Risk', value: compactCurrency(current.arr_at_risk || 0) },
    { label: 'Ending in 30 Days', value: (current.ending_soon || 0).toLocaleString() },
  ],
  kpiGridColumns: 4,

  renderCellValue: (col, row, { currencyFormatter: cf, formatEventDate, team }) => {
    switch (col.id) {
      case 'member_name': {
        const name = row.member_name || '(No name)';
        if (team?.package?.crm && row.fan_id) {
          return (
            <a href={`/teams/${team.id}/crm/fans/${row.fan_id}`} style={{ color: 'var(--sl-fg-brand, #1982c4)' }}>
              {name}
            </a>
          );
        }
        return name;
      }
      case 'email': return row.email || '—';
      case 'product_name': return row.product_name || '—';
      case 'venue_name': return row.venue_name || '—';
      case 'status': return row.display_status || '—';
      case 'amount': return cf.format(row.amount || 0);
      case 'interval': return row.interval_label || '—';
      case 'member_since': return <span className="text-nowrap">{row.member_since ? formatEventDate(row.member_since) : '—'}</span>;
      case 'canceled_on': return <span className="text-nowrap">{row.canceled_on ? formatEventDate(row.canceled_on) : '—'}</span>;
      case 'ends_at': return <span className="text-nowrap">{row.ends_at ? formatEventDate(row.ends_at) : '—'}</span>;
      default: return '—';
    }
  },
};

function ReportingMembershipsPendingCancelationsPage(props) {
  return <SalesDetailPage {...props} config={PENDING_CANCELATIONS_CONFIG} />;
}

export default ReportingMembershipsPendingCancelationsPage;
